import type { DataManagementService } from "../services/dataManagement";
import { dmRegionFromHub } from "../services/dataManagement/region";
import type { AccItemVersion } from "../services/dataManagement/types";
import type { ModelDerivativeService } from "../services/modelDerivative";

function pickViewableVersion(
  versions: AccItemVersion[]
): AccItemVersion | undefined {
  if (versions.length === 0) return undefined;
  const sorted = [...versions].sort((a, b) => {
    const ta = a.createTime ? Date.parse(a.createTime) : 0;
    const tb = b.createTime ? Date.parse(b.createTime) : 0;
    return tb - ta;
  });
  const withFileUrn = sorted.find(
    v =>
      v.id.startsWith("urn:adsk.wipprod:fs.file:") && v.id.includes("?version=")
  );
  return withFileUrn ?? sorted[0];
}

/**
 * Resolve the latest ACC item version URN and ensure Model Derivative
 * translation is ready for Forge Viewer.
 */
export async function resolveAccViewerUrn(
  dm: DataManagementService,
  md: ModelDerivativeService,
  projectId: string,
  itemId: string,
  hubRegion?: string,
  onProgress?: (message: string, percent?: number) => void
): Promise<string> {
  const region = dmRegionFromHub(hubRegion);
  console.info("[Vera:ACC] Resolving viewer URN", {
    projectId,
    itemId,
    hubRegion,
  });
  onProgress?.("Fetching file version…", 8);
  const versions = await dm.getItemVersions(projectId, itemId, hubRegion);
  console.info(`[Vera:ACC] Found ${versions.length} version(s)`);
  if (versions.length === 0) {
    throw new Error("No versions found for this ACC file.");
  }
  const latest = pickViewableVersion(versions);
  if (!latest) {
    throw new Error("No versions found for this ACC file.");
  }
  const versionUrn = latest.id;
  console.info("[Vera:ACC] Latest version URN:", versionUrn.slice(0, 64));
  if (!versionUrn.startsWith("urn:")) {
    throw new Error("Unexpected ACC version id — expected a design URN.");
  }
  onProgress?.("Checking model translation…", 12);
  await md.ensureViewable(versionUrn, region, onProgress);
  return versionUrn;
}
